import { ActiveBrandMap } from '@PetSureAustralia/petsure-react-ui-library/distLib/autoGenerated/activeBrandCssMap';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';

import { BrandConfigs } from '@PetSureAustralia/petsure-brand-partner-configs/distConfigs/brandConfigs';
import { BrandSwitch } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { Helmet } from 'react-helmet';
import { CSPBrandConfigs } from '../brands';
import { BrandContext } from './brandContext';

const useHelmetForCss = process.env.USE_HELMET_FOR_CSS;

export const BrandContextProvider = ({ brandConfig, env = 'prod', useMocks, showBrandSwitch, children }) => {
  const {
    theme: {
      brand: { key: brandKey }
    }
  } = brandConfig;

  const [activeBrandKey, setActiveBrandKey] = useState(brandKey);

  const [activeBrandContext, setActiveBrandContext] = useState({
    ...brandConfig.theme,
    // TEMPORARY - CSP Configs should be moved to BrandConfigs package
    ...(CSPBrandConfigs[brandKey] || CSPBrandConfigs.default)
  });

  const {
    cspConfiguration: { gtm }
  } = activeBrandContext;

  const applyBrandContext = useCallback(
    (newBrandContext) => {
      const {
        theme: {
          brand: { key: brandKey }
        }
      } = newBrandContext;

      if (brandKey !== activeBrandKey) {
        const newBrandKey = brandKey;
        setActiveBrandKey(newBrandKey);
        setActiveBrandContext({
          ...newBrandContext.theme,
          ...(CSPBrandConfigs[newBrandKey] || CSPBrandConfigs.default)
        });
      }
    },
    [activeBrandKey]
  );

  const brandProviderValue = useMemo(
    () => ({
      activeBrandContext,
      applyBrandContext,
      useMocks,
      showBrandSwitch,
      env
    }),
    [applyBrandContext, activeBrandContext, useMocks, showBrandSwitch, env]
  );

  const fallbackCssUrl = useMemo(() => {
    return `${ActiveBrandMap.classic}?${Date.now()}`;
  }, []);

  const brandCssUrl = useMemo(() => {
    return `${ActiveBrandMap[activeBrandKey]}?${Date.now()}`;
  }, [activeBrandKey]);

  return (
    <BrandContext.Provider value={brandProviderValue}>
      {useHelmetForCss && (
        <Helmet>
          {activeBrandContext && activeBrandContext.brand.key ? (
            <link
              rel="stylesheet"
              type="text/css"
              href={brandCssUrl}
            />
          ) : (
            <link
              rel="stylesheet"
              type="text/css"
              href={fallbackCssUrl}
            />
          )}

          {activeBrandContext.brand.cssUrls &&
            activeBrandContext.brand.cssUrls.map((cssUrl, index) => (
              <link
                key={`css_${brandKey}_${index + 1}`}
                rel="stylesheet"
                type="text/css"
                href={cssUrl}
              />
            ))}

          <script>
            {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','${gtm.dataLayer}','${gtm.id}');
            `}
          </script>
        </Helmet>
      )}
      {showBrandSwitch && (
        <BrandSwitch
          customContext={BrandContext}
          onBrandSelect={applyBrandContext}
          brandConfigs={BrandConfigs}
        />
      )}
      {children}
    </BrandContext.Provider>
  );
};

BrandContextProvider.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  brandConfig: PropTypes.any,
  env: PropTypes.oneOf(['sit', 'uat', 'preprod', 'prod', 'local']),
  useMocks: PropTypes.bool,
  showBrandSwitch: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.any
};

export default { BrandContext, BrandContextProvider };
